import { useMemo, useCallback, useState, useEffect } from 'react'
import useAppAuth from '../../AppAuthProvider/hooks/useAppAuth'
import {
    ActivationObjectProviderProps,
    ActivationObjectProviderValue,
    UpdateActivationObject,
    useActivationObjectProviderProps,
} from '../types'
import {
    useActivationObjectCreateMutation,
    useActivationObjectQuery,
    useActivationObjectUpdateMutation,
    useFinishMutation,
} from '../../../../api/activationObject/hooks'
import { useSnackbar } from 'notistack'
import { useActivationRedirect } from './useActivationRedirect'

const useActivationObjectProvider = (
    props: useActivationObjectProviderProps,
): ActivationObjectProviderProps => {
    const auth = useAppAuth()
    const { data: activationObject, isError } = useActivationObjectQuery(
        auth.synonym,
    )
    const [globalAvatarMuted, setGlobalAvatarMuted] = useState(true)
    const [isDocumentClicked, setIsDocumentClicked] = useState(false)
    const updateMutation = useActivationObjectUpdateMutation()
    const createMutation = useActivationObjectCreateMutation()
    const finishMutation = useFinishMutation()
    const [finishPcoObject] = useState(false)

    const error = useMemo(() => {
        if (createMutation.isError)
            return (
                'Chyba při zakládání aktivačního objektu: ' +
                createMutation.error.response?.data.detail
            )
        if (updateMutation.error)
            return (
                'Chyba při aktualizaci aktivačního objektu: ' +
                updateMutation.error.title
            )
    }, [
        createMutation.error?.response?.data.detail,
        createMutation.isError,
        updateMutation.error,
    ])

    const { enqueueSnackbar } = useSnackbar()

    useEffect(() => {
        if (error) enqueueSnackbar(error, { variant: 'error' })
    })

    useActivationRedirect({ activationObject, isDocumentClicked, isError })

    const create = useCallback(() => {
        if (auth.synonym) {
            createMutation.mutate(auth.synonym)
        }
    }, [auth.synonym, createMutation])

    const finish = useCallback(() => {
        if (auth.synonym) {
            finishMutation.mutate(auth.synonym)
        }
    }, [auth.synonym, finishMutation])

    const update = useCallback(
        (obj: UpdateActivationObject) => {
            updateMutation.mutate(obj)
        },
        [updateMutation],
    )

    const contextValue: ActivationObjectProviderValue = {
        data: activationObject,
        create,
        finish,
        update,
        finishPcoObject,
        isUpdating: updateMutation.isLoading || createMutation.isLoading,
        globalAvatarMuted,
        setGlobalAvatarMuted,
        isDocumentClicked,
        setIsDocumentClicked,
    }

    return {
        contextValue,
        error,
        notFound: isError,
    }
}

export default useActivationObjectProvider
